import React, { useCallback, useMemo } from 'react';
import ListView from 'components/RecordsListView/ListView';
import { FILTER_LABEL } from 'screens/Applications&WorkOrders/Components/Constants';
import SortDropdownMenu from 'components/RecordsListView/SortDropdownMenu';
import { ApplicationSummary } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import {
  usePagination,
  useSearch,
  useSort,
  useSummaryParams
} from 'components/RecordsListView/context/SummaryParamsContext';
import useListViewSelection from 'hooks/useListViewSelection';
import useListViewColumns from 'screens/Applications&WorkOrders/Hooks/useListViewColumns';
import FilterDrawer from 'components/RecordsListView/FilterDrawer';
import OperationDetailPanel from 'screens/Applications&WorkOrders/Components/OperationDetailPanel';
import { OperationType } from 'components/RecordsListView/constants';
import useOperationEvents from 'hooks/useOperationEvents';
import useTagsData from 'hooks/useTagsData';
import { ChildrenRecords } from 'components/RecordsCreatedIndicators/types';

export interface OperationListViewConfig {
  icon: React.ReactNode;
  title: string;
  searchPlaceholder: string;
  emptyMessage: string;
  operationType: OperationType;
  onCreateClick: () => void;
}

interface OperationListViewProps {
  items: ApplicationSummary[];
  totalElements: number;
  currentPage: number;
  pageSize: number;
  isLoading?: boolean;
  onItemDeleted?: (itemId: string) => void;
  hasFilters?: boolean;
  config: OperationListViewConfig;
}

const OperationListView: React.FC<OperationListViewProps> = ({
  items,
  totalElements,
  currentPage,
  pageSize,
  isLoading = false,
  hasFilters = false,
  onItemDeleted,
  config
}) => {
  const { sendViewEvent } = useOperationEvents(config.operationType);
  const { searchTerm, handleSearchChange } = useSearch();
  const { config: paginationConfig } = usePagination({
    currentPage,
    totalElements,
    pageSize
  });

  const {
    params,
    filters,
    isDrawerVisible: isFilterDrawerVisible,
    setIsDrawerVisible: setIsFilterDrawerVisible,
    handleFiltersChange
  } = useSummaryParams();

  useSort();

  const { tags: rawTags } = useTagsData();

  const { selectedId, handleSelect, handleItemDeleted } = useListViewSelection({
    items,
    isLoading
  });

  const handleFilterClick = () => {
    setIsFilterDrawerVisible(true);
  };

  const handleOperationDeleted = useCallback(
    (deletedId: string) => {
      // Deep-linked operationId pins the search box to this record's name; clear it too.
      if (params.id && params.id === deletedId) {
        handleSearchChange('');
      }
      handleItemDeleted(deletedId, onItemDeleted);
    },
    [handleItemDeleted, handleSearchChange, onItemDeleted, params.id]
  );

  const { rowSelection, columns } = useListViewColumns({
    selectedId,
    onSelectId: handleSelect,
    allTags: rawTags,
    searchTerm
  });

  const summaryChildrenRecords = useMemo<ChildrenRecords | undefined>(() => {
    if (!selectedId) return undefined;
    const selectedItem = items.find(item => item.id === selectedId);
    return selectedItem?.childrenRecords as ChildrenRecords | undefined;
  }, [items, selectedId]);

  return (
    <ListView
      icon={config.icon}
      sortDropdown={<SortDropdownMenu />}
      title={config.title}
      searchPlaceholder={config.searchPlaceholder}
      pagination={paginationConfig}
      searchText={searchTerm}
      onSearchChange={handleSearchChange}
      createButtonLabel="Create New"
      onCreateClick={config.onCreateClick}
      filterButtonLabel={FILTER_LABEL}
      onFilterClick={handleFilterClick}
      hasActiveFilters={hasFilters}
      dataSource={items}
      columns={columns}
      rowKey="id"
      rowSelection={rowSelection}
      onRowClick={record => {
        sendViewEvent();
        handleSelect(record.id);
      }}
      isLoading={isLoading}
      emptyMessage={config.emptyMessage}
      detailPanel={
        <OperationDetailPanel
          operationId={selectedId}
          operationType={config.operationType}
          isLoading={isLoading}
          onOperationDeleted={handleOperationDeleted}
          hasOperations={items.length > 0}
          summaryChildrenRecords={summaryChildrenRecords}
        />
      }
      filterDrawer={
        <FilterDrawer
          initialValues={filters}
          visible={isFilterDrawerVisible}
          onFiltersChange={handleFiltersChange}
          onClose={() => setIsFilterDrawerVisible(false)}
          operationType={config.operationType}
        />
      }
    />
  );
};

export default OperationListView;
